import { memo, useEffect, useRef } from 'react';
import { useTerminalStore, selectAllSignals } from '../../stores/terminal.store.js';
import { backtestUTBot } from '@arc/shared';

const CX = 80;
const CY = 76;

// Helper: point on circle
function pt(cx: number, cy: number, r: number, deg: number) {
  const rad = (deg - 90) * Math.PI / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

// Hexagon points at given radius
function hexPoints(cx: number, cy: number, r: number, rot = 0) {
  return [0, 60, 120, 180, 240, 300]
    .map(d => { const p = pt(cx, cy, r, d + rot); return `${p.x},${p.y}`; })
    .join(' ');
}

export const ArcReactor = memo(function ArcReactor() {
  const signals     = useTerminalStore(selectAllSignals);
  const wsConnected = useTerminalStore(s => s.wsConnected);
  const candles     = useTerminalStore(s => s.candles);
  const pctRef      = useRef<SVGTextElement>(null);

  const powerPct = Math.min(99.9, 90 + (signals.length % 10));

  const allCandles = Object.values(candles).flat();
  const bt = allCandles.length > 0 && signals.length > 0
    ? backtestUTBot(allCandles, signals)
    : null;

  useEffect(() => {
    if (pctRef.current) {
      pctRef.current.textContent = powerPct.toFixed(1) + '%';
    }
  }, [powerPct]);

  const stats = [
    { label: 'SIGNALS',   value: String(signals.length),                                         color: '#00d4ff' },
    { label: 'BUY',       value: String(signals.filter(s => s.type === 'BUY').length),            color: '#00ff9d' },
    { label: 'SELL',      value: String(signals.filter(s => s.type === 'SELL').length),           color: '#ff2d55' },
    { label: 'BUY ACC.',  value: bt ? bt.buyAccuracy.toFixed(1)  + '%' : 'N/A',                  color: '#00ff9d' },
    { label: 'SELL ACC.', value: bt ? bt.sellAccuracy.toFixed(1) + '%' : 'N/A',                  color: '#ff2d55' },
    { label: 'WS',        value: wsConnected ? 'ONLINE' : 'POLLING', color: wsConnected ? '#00ff9d' : '#FFD700' },
  ];

  // Power arc: circumference of r=62 is 2π×62≈389.6
  const arcR   = 62;
  const arcC   = 2 * Math.PI * arcR;
  const arcFill = arcC * (powerPct / 100);

  return (
    <div className="panel h-full flex flex-col">
      <div className="panel-header">
        <span className="font-orb text-[11px] font-bold tracking-[2px] flex items-center gap-1.5"
              style={{ color: 'var(--color-arc)', textShadow: '0 0 8px var(--color-arc)' }}>
          <span style={{ width:'2px', height:'10px', background:'var(--color-arc)',
                         boxShadow:'0 0 4px var(--color-arc)', display:'inline-block', flexShrink:0 }}/>
          ARC REACTOR
        </span>
        <span className="badge ml-2" style={{
          clipPath: 'polygon(3px 0%,100% 0%,calc(100% - 3px) 100%,0% 100%)',
          background: wsConnected ? 'rgba(0,255,157,0.15)' : 'rgba(255,215,0,0.12)',
          color: wsConnected ? '#00ff9d' : '#FFD700',
          border: `1px solid ${wsConnected ? 'rgba(0,255,157,0.4)' : 'rgba(255,215,0,0.35)'}`,
          fontSize: '11px', padding: '1px 6px', letterSpacing: '1.5px',
        }}>
          {wsConnected ? 'WS LIVE' : 'POLLING'}
        </span>
        <span className="label-mono ml-auto text-[11px] opacity-90">SYSTEM STATUS</span>
      </div>

      <div className="flex-1 flex flex-col items-center justify-between p-2 gap-2 min-h-0">

        {/* ─── Reactor SVG ─────────────────────────────────────────────── */}
        <svg viewBox="0 0 160 152" className="w-full flex-shrink-0" style={{ maxHeight: '52%' }}
             xmlns="http://www.w3.org/2000/svg">
          <defs>
            <filter id="rg-glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="2.5" result="blur"/>
              <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
            </filter>
            <filter id="rg-core" x="-100%" y="-100%" width="300%" height="300%">
              <feGaussianBlur stdDeviation="5" result="blur"/>
              <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
            </filter>
            <radialGradient id="core-grad" cx="50%" cy="50%">
              <stop offset="0%"   stopColor="#ffffff" stopOpacity="1"/>
              <stop offset="30%"  stopColor="var(--color-arc)" stopOpacity="1"/>
              <stop offset="100%" stopColor="var(--color-arc)" stopOpacity="0.1"/>
            </radialGradient>
            <radialGradient id="bg-grad" cx="50%" cy="50%">
              <stop offset="0%"   stopColor="#021828" stopOpacity="1"/>
              <stop offset="100%" stopColor="#010810" stopOpacity="1"/>
            </radialGradient>
          </defs>

          {/* Background fill */}
          <circle cx={CX} cy={CY} r="74" fill="url(#bg-grad)" />

          {/* ── Layer 1: Outermost ring — slow CCW, 24 ticks ──────────── */}
          <g style={{ transformOrigin:`${CX}px ${CY}px`, animation:'counterSpin 16s linear infinite' }}>
            <circle cx={CX} cy={CY} r="73" fill="none" stroke="var(--color-arc)" strokeWidth="0.5" opacity="0.2"/>
            {Array.from({length:24}, (_,i) => {
              const deg = i * 15;
              const long = i % 6 === 0;
              const med  = i % 3 === 0 && !long;
              const p1 = pt(CX, CY, 73, deg);
              const p2 = pt(CX, CY, long ? 67 : med ? 69 : 70.5, deg);
              return (
                <line key={i} x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
                  stroke="var(--color-arc)"
                  strokeWidth={long ? 1.5 : med ? 0.8 : 0.4}
                  opacity={long ? 1 : med ? 0.6 : 0.3}
                />
              );
            })}
            {/* 4 arc segments with gaps */}
            <circle cx={CX} cy={CY} r="71" fill="none" stroke="var(--color-arc)" strokeWidth="1.2"
              strokeDasharray="25 11.1" opacity="0.45"/>
          </g>

          {/* ── Layer 2: Power arc ring — static, shows power % ───────── */}
          <circle cx={CX} cy={CY} r={arcR} fill="none"
            stroke="rgba(0,212,255,0.08)" strokeWidth="4"/>
          <circle cx={CX} cy={CY} r={arcR} fill="none"
            stroke="url(#arc-power)" strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray={`${arcFill} ${arcC - arcFill}`}
            style={{ transformOrigin:`${CX}px ${CY}px`, transform:'rotate(-90deg)' }}
          />
          <defs>
            <linearGradient id="arc-power" gradientUnits="userSpaceOnUse"
              x1={CX} y1={CY - arcR} x2={CX + arcR} y2={CY}>
              <stop offset="0%"   stopColor="#00d4ff" stopOpacity="0.9"/>
              <stop offset="100%" stopColor="#FFD700" stopOpacity="0.8"/>
            </linearGradient>
          </defs>

          {/* ── Layer 3: Medium ring — orbital nodes, CW ──────────────── */}
          <g style={{ transformOrigin:`${CX}px ${CY}px`, animation:'coreSpin 7s linear infinite' }}>
            <circle cx={CX} cy={CY} r="54" fill="none" stroke="var(--color-arc)"
              strokeWidth="0.5" strokeDasharray="3 3" opacity="0.15"/>
            {[0,45,90,135,180,225,270,315].map((deg,i) => {
              const p = pt(CX, CY, 54, deg);
              const isPrimary = i % 2 === 0;
              return (
                <circle key={deg} cx={p.x} cy={p.y}
                  r={isPrimary ? 2.2 : 1.2}
                  fill="var(--color-arc)"
                  filter={isPrimary ? 'url(#rg-glow)' : undefined}
                  opacity={isPrimary ? 1 : 0.4}
                />
              );
            })}
          </g>

          {/* ── Layer 4: Static heavy hex border ──────────────────────── */}
          <polygon points={hexPoints(CX, CY, 48, 0)}
            fill="rgba(2,20,36,0.8)" stroke="var(--color-border)" strokeWidth="6"/>
          <polygon points={hexPoints(CX, CY, 48, 0)}
            fill="none" stroke="var(--color-arc)" strokeWidth="1" opacity="0.6"
            filter="url(#rg-glow)"/>
          {/* Hex corner diamonds */}
          {[0,60,120,180,240,300].map(deg => {
            const p = pt(CX, CY, 48, deg);
            return (
              <polygon key={deg}
                points={`${p.x},${p.y-3} ${p.x+2},${p.y} ${p.x},${p.y+3} ${p.x-2},${p.y}`}
                fill="var(--color-arc)" opacity="0.8"
              />
            );
          })}

          {/* ── Layer 5: Inner hex — fast CCW ─────────────────────────── */}
          <g style={{ transformOrigin:`${CX}px ${CY}px`, animation:'counterSpin 3.5s linear infinite' }}>
            <polygon points={hexPoints(CX, CY, 34, 30)}
              fill="none" stroke="var(--color-arc)" strokeWidth="1" opacity="0.5"/>
          </g>

          {/* ── Layer 6: Tri-blade rotor — fast CW ────────────────────── */}
          <g style={{ transformOrigin:`${CX}px ${CY}px`, animation:'coreSpin 2s linear infinite' }}>
            {[0, 120, 240].map(deg => {
              const tip = pt(CX, CY, 26, deg);
              const l   = pt(CX, CY, 10, deg - 45);
              const r2  = pt(CX, CY, 10, deg + 10);
              return (
                <path key={deg}
                  d={`M ${CX} ${CY} L ${tip.x} ${tip.y} Q ${l.x} ${l.y} ${r2.x} ${r2.y} Z`}
                  fill="var(--color-arc)"
                  fillOpacity="0.2"
                  stroke="var(--color-arc)"
                  strokeWidth="0.7"
                  filter="url(#rg-glow)"
                />
              );
            })}
            {/* Small center ring on rotor */}
            <circle cx={CX} cy={CY} r="6" fill="none" stroke="var(--color-arc)" strokeWidth="1" opacity="0.4"/>
          </g>

          {/* ── Core: pulsing bright circle ───────────────────────────── */}
          <circle cx={CX} cy={CY} r="10" fill="url(#core-grad)"
            filter="url(#rg-core)"
            style={{ animation:'reactorCorePulse 2s ease-in-out infinite' }}/>
          {/* White hot center */}
          <circle cx={CX} cy={CY} r="4" fill="#ffffff" opacity="0.95"/>

          {/* ── Power readout text ─────────────────────────────────────── */}
          <text x={CX} y="132" textAnchor="middle"
            fill="var(--color-arc)" fontSize="6.5" opacity="0.5"
            fontFamily="'Share Tech Mono',monospace" letterSpacing="2">
            POWER OUTPUT
          </text>
          <text ref={pctRef} x={CX} y="144" textAnchor="middle"
            fill="#FFD700" fontSize="13"
            fontFamily="'Share Tech Mono',monospace" fontWeight="700"
            filter="url(#rg-glow)">
            {powerPct.toFixed(1)}%
          </text>
        </svg>

        {/* ─── Stats ───────────────────────────────────────────────────── */}
        <div className="w-full font-mono text-[11px] flex flex-col gap-px flex-shrink-0">
          {stats.map(({ label, value, color }) => (
            <div key={label}
                 className="flex justify-between items-center px-2 py-[3px]"
                 style={{ borderBottom: '1px solid rgba(13,42,64,0.8)' }}>
              <span style={{ color: 'rgba(127,179,211,0.6)', letterSpacing: '1.5px' }}>{label}</span>
              <span style={{ color, fontWeight: 700, letterSpacing: '1px',
                             textShadow: `0 0 6px ${color}` }}>{value}</span>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes reactorCorePulse {
          0%,100% { opacity:0.9; transform:scale(1); }
          50%      { opacity:1;   transform:scale(1.15); }
        }
      `}</style>
    </div>
  );
});
